import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, viewChild } from '@angular/core'
import { ColorName } from '../design-system'
import { Icon, IconComponent, IconSizePx } from '../icon'
import { TooltipAlignment, TooltipModule, TooltipPosition } from '../tooltip'

/**
 * A simple button component used for displaying an icon without text. Content in the button is
 * visually hidden, but accessible to screen readers and rendered as a tooltip on hover/focus.
 *
 * @example
 * ``` html
 * <button
 *   ftrIconButton
 *   icon="More"
 *   size="small"
 *   class="custom-class"
 *   [isCircle]="true"
 *   (click)="doSomething($event)"
 * >
 *   Tooltip content
 * </button>
 * ```
 */
@Component({
  selector: 'button[ftrIconButton], a[ftrIconButton]',
  templateUrl: './icon-button.component.html',
  styleUrl: './icon-button.component.css',
  host: {
    '[class]': 'classes()',
    '[aria-pressed]': 'selected()',
    // Explicit aria-label so automated a11y scanners consistently pick up the name
    // (visually-hidden projected content alone is sometimes reported as unnamed / "icon").
    '[attr.aria-label]': 'tooltipMessage() || null',
  },
  imports: [IconComponent, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconButtonComponent {
  readonly icon = input.required<Icon>()
  readonly iconColor = input<ColorName>('Grey50')
  readonly size = input<IconButtonSize>('medium')
  readonly withOutline = input<boolean>(false)
  readonly isCircle = input<boolean>(false)
  readonly isDestructive = input<boolean>(false)
  readonly tooltipPosition = input<TooltipPosition>('above')
  readonly tooltipAlignment = input<TooltipAlignment>('auto')
  readonly selected = input<boolean>()

  protected readonly hostElement: ElementRef<HTMLButtonElement> = inject(ElementRef<HTMLButtonElement>)

  // A workaround to get the tooltip from the button's content, which is visually hidden but still rendered in the DOM
  // for screen readers and to avoid a change to the API to require a separate input for the tooltip.
  protected readonly buttonContent = viewChild.required<ElementRef<HTMLElement>>('buttonContent')
  protected readonly tooltipMessage = computed(() => {
    return this.buttonContent()?.nativeElement.textContent?.trim() ?? ''
  })

  protected readonly classes = computed(() => ({
    'icon-button': true,
    'icon-button--small': this.size() === 'small',
    'icon-button--large': this.size() === 'large',
    'icon-button--outline': this.withOutline(),
    'icon-button--circle': this.isCircle(),
  }))

  protected readonly iconSize = computed<IconSizePx>(() => (this.size() === 'small' ? 18 : 22))
}

export const IconButtonSizes = ['small', 'medium', 'large'] as const
export type IconButtonSize = (typeof IconButtonSizes)[number]
